import type { CardWarp } from "../transitions/CardWarp";

export type RouteName = "home" | "about" | "link";
export type ViewRenderer = (mount: HTMLElement) => void;

export interface RouterDeps {
  cardWarp: CardWarp;
  overlay: HTMLElement;
  navButtons: HTMLButtonElement[];
  views: Record<Exclude<RouteName, "home">, ViewRenderer>;
}

const PATH_TO_ROUTE: Record<string, RouteName> = {
  "/": "home",
  "/about": "about",
  "/link": "link",
};

const ROUTE_TO_PATH: Record<RouteName, string> = {
  home: "/",
  about: "/about",
  link: "/link",
};

/**
 * Logical left-to-right position of each route. Used to compute the
 * direction a slide transition should travel when moving between pages.
 */
const ROUTE_ORDER: Record<RouteName, number> = {
  about: -1,
  home: 0,
  link: 1,
};

export type SlideDirection = "left" | "right" | "none";

function computeDirection(prev: RouteName, next: RouteName): SlideDirection {
  const diff = ROUTE_ORDER[next] - ROUTE_ORDER[prev];
  if (diff > 0) return "right";
  if (diff < 0) return "left";
  return "none";
}

export class Router {
  private readonly deps: RouterDeps;
  private current: RouteName = "home";

  constructor(deps: RouterDeps) {
    this.deps = deps;
  }

  start(): void {
    window.addEventListener("popstate", this.onPopState);
    const initial = this.routeFromLocation();
    if (initial !== "home") {
      void this.applyRoute(initial, { push: false });
    } else {
      this.syncNav("home");
    }
  }

  go(path: string): void {
    const route = PATH_TO_ROUTE[path] ?? "home";
    if (route === this.current) return;
    void this.applyRoute(route, { push: true });
  }

  get currentRoute(): RouteName {
    return this.current;
  }

  private onPopState = (): void => {
    const route = this.routeFromLocation();
    if (route === this.current) return;
    void this.applyRoute(route, { push: false });
  };

  private async applyRoute(route: RouteName, opts: { push: boolean }): Promise<void> {
    const previous = this.current;
    this.current = route;
    if (opts.push) {
      window.history.pushState({ route }, "", ROUTE_TO_PATH[route]);
    }

    this.syncNav(route);

    if (route === "home") {
      // Keep the existing dataset.direction so the leaving page's CSS
      // animation does not re-trigger mid-exit. Mark as exiting so the
      // page-section children play their slide-out keyframes in parallel
      // with the overlay fade-out.
      const exitDirection = this.readDirection();
      this.deps.overlay.dataset["exiting"] = "true";
      try {
        await this.deps.cardWarp.exit(exitDirection);
      } finally {
        delete this.deps.overlay.dataset["exiting"];
      }
      return;
    }

    const direction = computeDirection(previous, route);
    this.deps.overlay.dataset["direction"] = direction;

    if (previous !== "home") {
      this.renderView(route);
      return;
    }

    this.renderView(route);
    await this.deps.cardWarp.enter(route, direction);
  }

  private readDirection(): SlideDirection {
    const raw = this.deps.overlay.dataset["direction"];
    if (raw === "left" || raw === "right") return raw;
    return "none";
  }

  private renderView(route: RouteName): void {
    if (route === "home") return;
    const renderer = this.deps.views[route];
    this.deps.overlay.replaceChildren();
    renderer(this.deps.overlay);
  }

  private routeFromLocation(): RouteName {
    return PATH_TO_ROUTE[window.location.pathname] ?? "home";
  }

  private syncNav(route: RouteName): void {
    const targetPath = ROUTE_TO_PATH[route];
    for (const btn of this.deps.navButtons) {
      const path = btn.dataset["route"];
      if (!path) continue;
      if (path === targetPath) {
        btn.setAttribute("aria-current", "page");
      } else {
        btn.removeAttribute("aria-current");
      }
    }
  }
}
